import { useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Boxes, Loader2, CheckCircle, XCircle, ShieldCheck, Save, RotateCcw, Lightbulb, Battery } from "lucide-react";
import { generateScenarios, describeStock } from "@/lib/batteryBalancer";

const PACK_SIZES = [28, 32];
const VEHICLE_LABEL = { 28: "Toyota / Auris / Yaris", 32: "Lexus IS300h" };

// Verdict global du pack.
const LEVEL_STYLES = {
  Excellent: "border-neon-green/40 text-neon-green bg-neon-green-muted",
  "Tres bon": "border-neon-cyan/40 text-neon-cyan bg-neon-cyan-muted",
  Bon: "border-neon-blue/40 text-neon-blue bg-neon-blue-muted",
  "A revoir": "border-neon-yellow/40 text-neon-yellow bg-neon-yellow-muted",
  "Non equilibre": "border-neon-red/40 text-neon-red bg-neon-red-muted",
};
const VERDICT_LABEL = { Excellent: "Excellent", "Tres bon": "Très bon", Bon: "Bon", "A revoir": "À revoir", "Non equilibre": "Pack non équilibré" };
const STATUS_LABEL = { Excellent: "Excellent", "Tres bon": "Très bon", Bon: "Bon", Tolerable: "Tolérable", Mauvais: "Mauvais", Critique: "Critique" };

// Statut interne d'un bloc (le pire entre delta capacite et delta IR).
const BLOCK_STATUS_STYLES = {
  Excellent: "text-neon-green",
  "Tres bon": "text-neon-cyan",
  Bon: "text-neon-blue",
  Tolerable: "text-neon-yellow",
  Mauvais: "text-neon-red",
  Critique: "text-neon-red",
};

const TIER_STYLES = {
  "Pack A": "text-neon-green",
  "Pack B": "text-neon-cyan",
  "Pack C": "text-neon-blue",
};

function TH({ children, className = "" }) {
  return <TableHead className={`bg-cyber-elevated text-[10px] font-mono uppercase tracking-wider text-muted-foreground whitespace-nowrap ${className}`}>{children}</TableHead>;
}
function TD({ children, className = "" }) {
  return <TableCell className={`font-mono text-sm whitespace-nowrap ${className}`}>{children}</TableCell>;
}

function CellTag({ cell, status }) {
  const num = cell?.original?.cell_number ?? cell?.id;
  return (
    <span className="inline-flex items-center gap-1">
      {status === true && <CheckCircle className="w-3 h-3 text-neon-green" />}
      {status === false && <XCircle className="w-3 h-3 text-neon-red" />}
      <span className="text-neon-blue font-bold">{num}</span>
    </span>
  );
}

export default function GenerateBlocPage() {
  const [packSize, setPackSize] = useState(null);
  const [minCapacity, setMinCapacity] = useState("");
  const [generating, setGenerating] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [name, setName] = useState("");
  const [verifyMap, setVerifyMap] = useState(null); // uuid -> bool (Disponible ?)
  const [verifying, setVerifying] = useState(false);
  const [creating, setCreating] = useState(false);
  const [stock, setStock] = useState(null); // bilan du stock au dernier chargement

  const result = scenarios[selectedIdx] || null;

  const reset = () => {
    setPackSize(null);
    setScenarios([]);
    setSelectedIdx(0);
    setName("");
    setVerifyMap(null);
  };

  const fetchAvailable = useCallback(async () => {
    const { data } = await api.get("/api/cells/available");
    return { cells: data.cells || [], hidden: data.hidden_by_pack || { count: 0, series: {} } };
  }, []);

  const selectScenario = (idx) => {
    setSelectedIdx(idx);
    setVerifyMap(null);
    const s = scenarios[idx];
    if (s) setName(`Bloc ${s.packSize} ${s.tier} - ${new Date().toLocaleDateString("fr-FR")}`);
  };

  const generate = async (size) => {
    setPackSize(size);
    setScenarios([]);
    setSelectedIdx(0);
    setVerifyMap(null);
    setGenerating(true);
    try {
      const { cells, hidden } = await fetchAvailable();
      setStock({ ...describeStock(cells, { minCapacity }), hidden });
      const list = generateScenarios(cells, { packSize: size, minCapacity });
      setScenarios(list);
      setSelectedIdx(0);
      if (list[0]) setName(`Bloc ${size} ${list[0].tier} - ${new Date().toLocaleDateString("fr-FR")}`);
    } catch (err) {
      const msg = err?.message || err?.response?.data?.detail || "Erreur de generation";
      toast.error(msg);
      setPackSize(null);
    } finally {
      setGenerating(false);
    }
  };

  const verify = async () => {
    if (!result) return;
    setVerifying(true);
    try {
      const { cells } = await fetchAvailable();
      const availableIds = new Set(cells.map((c) => c.id));
      const map = {};
      for (const c of result.cells) map[c.original.id] = availableIds.has(c.original.id);
      setVerifyMap(map);
      const missing = Object.values(map).filter((v) => !v).length;
      if (missing === 0) toast.success("Toutes les cellules sont disponibles");
      else toast.warning(`${missing} cellule(s) ne sont plus disponibles`);
    } catch {
      toast.error("Erreur lors de la verification");
    } finally {
      setVerifying(false);
    }
  };

  const allVerifiedOk = verifyMap && result && result.cells.every((c) => verifyMap[c.original.id]);

  const createPack = async () => {
    if (!result || !name.trim()) return;
    setCreating(true);
    try {
      const cell_ids = result.cells.map((c) => c.original.id); // ordre = position physique (negatif -> positif)
      await api.post("/api/packs", {
        name: name.trim(),
        description: `Genere automatiquement (par blocs) - ${result.title} - niveau ${result.stats.level}`,
        cell_ids,
        pack_size: result.packSize,
        level: result.stats.level,
      });
      toast.success(`Pack "${name.trim()}" cree — ${cell_ids.length} cellules mises en indisponible`);
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la creation du pack");
    } finally {
      setCreating(false);
    }
  };

  const stats = result?.stats;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl text-white flex items-center gap-2"><Boxes className="w-6 h-6 text-neon-blue" />Generate Bloc</h1>
        <p className="text-sm text-muted-foreground font-mono mt-1">Pack equilibre par paires de 2 cellules — le calcul se fait par bloc.</p>
      </div>

      {/* Etape 1 : choix de la taille */}
      <Card className="bg-cyber-surface border-neon-blue/20 rounded-xl">
        <CardHeader><CardTitle className="font-heading text-white text-base">1. Choisir la taille du bloc</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {PACK_SIZES.map((size) => (
              <Button
                key={size}
                onClick={() => generate(size)}
                disabled={generating}
                variant={packSize === size ? "default" : "outline"}
                className={packSize === size
                  ? "h-auto min-h-[4.5rem] py-3 text-lg font-bold rounded-xl bg-gradient-to-r from-neon-blue to-neon-purple text-white neon-glow-blue"
                  : "h-auto min-h-[4.5rem] py-3 text-lg font-bold rounded-xl border-neon-blue/30 bg-transparent text-white hover:bg-cyber-highlight"}
              >
                {generating && packSize === size
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <span className="flex flex-col items-center gap-0.5 leading-tight">
                      <span>{size}</span>
                      <span className="text-[10px] font-mono uppercase text-muted-foreground whitespace-normal text-center">{size / 2} blocs</span>
                      <span className="text-[10px] font-mono uppercase text-muted-foreground whitespace-normal text-center">{VEHICLE_LABEL[size] || "cellules"}</span>
                    </span>}
              </Button>
            ))}
          </div>
          {stock && (
            <div className="border border-neon-blue/15 rounded-lg p-3 bg-cyber-bg/40 space-y-1 font-mono text-xs">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stock pris en compte</div>
              <div className="text-white"><span className="text-neon-green font-bold">{stock.usable}</span> cellule(s) utilisable(s) sur {stock.available} disponible(s)</div>
              {stock.noMeasure.count > 0 && (
                <div className="text-neon-yellow">⚠ {stock.noMeasure.count} sans capacite ou IR (non utilisables) — series : {Object.entries(stock.noMeasure.series).map(([k, v]) => `${k} (${v})`).join(", ")}. Renseignez leurs mesures dans la bibliotheque.</div>
              )}
              {stock.hidden?.count > 0 && (
                <div className="text-muted-foreground">{stock.hidden.count} masquee(s) : meme numero qu'une cellule deja dans un pack — series : {Object.entries(stock.hidden.series).map(([k, v]) => `${k} (${v})`).join(", ")}.</div>
              )}
              {stock.duplicates > 0 && <div className="text-muted-foreground">{stock.duplicates} doublon(s) de numero ignore(s) (un seul exemplaire par numero).</div>}
              {stock.belowMin > 0 && <div className="text-muted-foreground">{stock.belowMin} sous la capacite minimale.</div>}
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Capacite min. (mAh) — optionnel</Label>
              <Input value={minCapacity} onChange={(e) => setMinCapacity(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="ex: 4000" className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg w-44" />
            </div>
            {packSize && !generating && (
              <Button onClick={() => generate(packSize)} variant="outline" className="border-neon-blue/30 bg-transparent text-white rounded-lg h-10"><RotateCcw className="w-4 h-4 mr-2" />Regenerer</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selecteur de scenario par niveau de capacite */}
      {scenarios.length > 0 && (
        <Card className="bg-cyber-surface border-neon-blue/20 rounded-xl">
          <CardHeader><CardTitle className="font-heading text-white text-base">Choisir une proposition</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {scenarios.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => selectScenario(idx)}
                className={`text-left border rounded-xl p-3 transition-colors ${idx === selectedIdx ? "border-neon-blue bg-neon-blue-muted neon-glow-blue" : "border-neon-blue/15 bg-cyber-bg/50 hover:bg-cyber-highlight"}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-heading text-sm ${TIER_STYLES[s.tier]?.split(" ").find(c => c.startsWith("text")) || "text-white"}`}>{s.title}</span>
                  <Badge variant="outline" className={`font-mono text-[9px] uppercase ${LEVEL_STYLES[s.stats.verdict] || ""}`}>{VERDICT_LABEL[s.stats.verdict] || s.stats.verdict}</Badge>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{s.label}</div>
                <div className="font-mono text-sm text-white mt-1.5 flex items-center gap-1"><Battery className="w-3.5 h-3.5 text-neon-cyan" />{s.avgCapacity} mAh <span className="text-muted-foreground text-xs">moy/cellule</span></div>
                <div className="font-mono text-[11px] text-muted-foreground">delta global : {s.stats.capacityTotal.delta} mAh · pire interne : {s.stats.internalCap.max} mAh</div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {result && stats && (
        <>
          {/* Etape 2 : niveau + equilibrage global + analyse */}
          <Card className="bg-cyber-surface border-neon-blue/20 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-white text-base">2. {result.title} — {result.packSize} cellules / {result.totalBlocks} blocs</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs uppercase border-neon-cyan/40 text-neon-cyan bg-neon-cyan-muted">Qualite {stats.qualityPercent}%</Badge>
                <Badge variant="outline" className={`font-mono text-xs uppercase ${LEVEL_STYLES[stats.verdict] || ""}`}>{VERDICT_LABEL[stats.verdict] || stats.verdict}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {stats.warning && (
                <div className="flex items-start gap-2 border border-neon-red/50 bg-neon-red-muted rounded-lg p-3 neon-glow-red">
                  <XCircle className="w-4 h-4 text-neon-red mt-0.5 shrink-0" />
                  <span className="font-mono text-xs font-bold text-neon-red uppercase tracking-wide">{stats.warning}</span>
                </div>
              )}
              {stats.pairConstraintViolations > 0 && (
                <div className="flex items-start gap-2 border border-neon-yellow/30 bg-neon-yellow-muted rounded-lg p-3">
                  <XCircle className="w-4 h-4 text-neon-yellow mt-0.5 shrink-0" />
                  <span className="font-mono text-xs text-neon-yellow">{stats.pairConstraintViolations} bloc(s) ont un ecart interne &gt; {stats.maxPairCapacityDelta} mAh — cellules trop dispersees. Essayez un autre scenario ou une capacite min.</span>
                </div>
              )}
              {result.why?.length > 0 && (
                <div className="border border-neon-green/20 rounded-lg p-3 bg-neon-green-muted/30">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-neon-green mb-2">Pourquoi ce pack ?</div>
                  <ul className="space-y-1">
                    {result.why.map((line, i) => <li key={i} className="font-mono text-xs text-white">{line}</li>)}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: "Capacite totale / bloc (mAh)", s: stats.capacityTotal },
                  { label: "IR totale / bloc (mOhm)", s: stats.irTotal },
                  { label: "Temps moyen / bloc", s: stats.timeMean },
                ].map(({ label, s }) => (
                  <div key={label} className="border border-neon-blue/10 rounded-lg p-3 bg-cyber-bg/50">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
                    <div className="font-mono text-sm text-white">min {s.min} · max {s.max}</div>
                    <div className="font-mono text-sm text-white">delta entre blocs : <span className="text-neon-blue">{s.delta}</span></div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Cellules / blocs", value: `${result.packSize} / ${result.totalBlocks}`, good: true },
                  { label: "Capacite moy. cellule", value: `${stats.avgCellCapacity} mAh`, good: true },
                  { label: "Capacite moy. bloc", value: `${stats.avgBlockCapacity} mAh`, good: true },
                  { label: "Bloc le plus faible", value: `Bloc ${stats.weakestBlock.blockNumber} — ${stats.weakestBlock.capacity} mAh`, good: true },
                  { label: "Bloc le plus fort", value: `Bloc ${stats.strongestBlock.blockNumber} — ${stats.strongestBlock.capacity} mAh`, good: true },
                  { label: "Delta global capacite", value: `${stats.capacityTotal.delta} mAh`, good: stats.capacityTotal.delta <= 500 },
                  { label: "Pire delta interne", value: `${stats.internalCap.max} mAh`, good: stats.internalCap.max <= 200 },
                  { label: "Delta interne moyen", value: `${stats.internalCap.mean} mAh`, good: stats.internalCap.mean <= 100 },
                  { label: "Delta global IR", value: `${stats.irTotal.delta}`, good: stats.irTotal.delta <= 1.5 },
                  { label: "Pire delta IR interne", value: `${stats.internalIR.max}`, good: stats.internalIR.max <= 0.7 },
                  { label: "Qualite", value: `${stats.qualityPercent} %`, good: stats.qualityPercent >= 75 },
                  { label: "Verdict", value: VERDICT_LABEL[stats.verdict] || stats.verdict, good: ["Excellent", "Tres bon"].includes(stats.verdict) },
                ].map(({ label, value, good }) => (
                  <div key={label} className="border border-neon-blue/10 rounded-lg p-3 bg-cyber-bg/50">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
                    <div className={`font-mono text-sm font-bold ${good ? "text-neon-green" : "text-neon-yellow"}`}>{value}</div>
                  </div>
                ))}
              </div>
              {result.alerts?.length > 0 && (
                <div className="border border-neon-yellow/30 rounded-lg p-3 bg-neon-yellow-muted space-y-1">
                  {result.alerts.map((a, i) => (
                    <div key={i} className={`font-mono text-xs ${a.level === "critical" ? "text-neon-red font-bold" : "text-neon-yellow"}`}>{a.level === "critical" ? "⛔ " : "⚠ "}{a.text}</div>
                  ))}
                </div>
              )}
              {result.analysis?.length > 0 && (
                <div className="border border-neon-blue/10 rounded-lg p-3 bg-cyber-bg/40">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2"><Lightbulb className="w-3.5 h-3.5 text-neon-yellow" />Analyse automatique</div>
                  <ul className="space-y-1">
                    {result.analysis.map((line, i) => (
                      <li key={i} className="font-mono text-xs text-muted-foreground flex gap-2"><span className="text-neon-blue">•</span><span>{line}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tableau des blocs / paires */}
          <Card className="bg-cyber-surface border-neon-blue/20 rounded-xl">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="border-neon-blue/10 hover:bg-transparent">
                    <TH>Bloc</TH><TH>Pos. (-)</TH><TH>Cell. A</TH><TH>Cell. B</TH>
                    <TH>Cap A</TH><TH>Cap B</TH><TH>Cap totale</TH><TH>Cap moy</TH>
                    <TH>IR A</TH><TH>IR B</TH><TH>IR totale</TH>
                    <TH>T A</TH><TH>T B</TH><TH>T moy</TH>
                    <TH>Δ Cap</TH><TH>Δ IR</TH><TH>Statut</TH><TH>Alertes</TH>
                  </TableRow></TableHeader>
                  <TableBody>
                    {result.blocks.map((b) => {
                      const vA = verifyMap ? verifyMap[b.cellA.original.id] : null;
                      const vB = verifyMap ? verifyMap[b.cellB.original.id] : null;
                      return (
                        <TableRow key={b.blockNumber} className="border-neon-blue/5">
                          <TD className="text-white font-bold">{b.blockNumber}</TD>
                          <TD className="text-muted-foreground text-xs">{b.positionFromNegative.join(" / ")}</TD>
                          <TD><CellTag cell={b.cellA} status={vA} /></TD>
                          <TD><CellTag cell={b.cellB} status={vB} /></TD>
                          <TD>{b.capacityA}</TD><TD>{b.capacityB}</TD>
                          <TD className="text-neon-cyan font-bold">{b.capacityTotal}</TD><TD>{b.capacityMean}</TD>
                          <TD>{b.irA}</TD><TD>{b.irB}</TD><TD className="text-neon-cyan">{b.irTotal}</TD>
                          <TD>{b.timeA}</TD><TD>{b.timeB}</TD><TD>{b.timeMean}</TD>
                          <TD className={b.deltaCapacity > stats.maxPairCapacityDelta ? "text-neon-red font-bold" : "text-muted-foreground"}>{b.deltaCapacity}</TD>
                          <TD className={b.deltaIR > 1 ? "text-neon-red font-bold" : "text-muted-foreground"}>{b.deltaIR}</TD>
                          <TD className={`font-bold ${BLOCK_STATUS_STYLES[b.blockStatus] || "text-muted-foreground"}`}>{STATUS_LABEL[b.blockStatus] || b.blockStatus}</TD>
                          <TD className="text-[11px]">{b.critical && <span className="text-neon-red font-bold">CRITIQUE </span>}{b.measureCheck && <span className="text-neon-yellow">Mesure à vérifier</span>}</TD>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Etape 3 : verification + creation */}
          <Card className="bg-cyber-surface border-neon-blue/20 rounded-xl">
            <CardHeader><CardTitle className="font-heading text-white text-base">3. Verifier et creer le pack</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5 max-w-md">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Nom du pack</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg" />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={verify} disabled={verifying} variant="outline" className="border-neon-blue/30 bg-transparent text-white rounded-lg">
                  {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Verifier la disponibilite
                </Button>
                <Button onClick={createPack} disabled={creating || !allVerifiedOk || !name.trim()} className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold rounded-lg neon-glow-blue">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Valider & creer le pack
                </Button>
                <Button onClick={reset} variant="ghost" className="text-muted-foreground rounded-lg"><RotateCcw className="w-4 h-4 mr-2" />Annuler</Button>
              </div>
              {!allVerifiedOk && (
                <p className="text-xs font-mono text-muted-foreground">
                  {verifyMap ? "Certaines cellules ne sont plus disponibles — relancez une generation." : "Verifiez la disponibilite avant de creer le pack."}
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
